import { useTranslation } from "react-i18next";
import React, { useState, useMemo } from "react";
import { Layout } from "@/components/layout";
import { useLocation } from "wouter";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { FileText, Trash2 } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { formatDate } from "@/lib/date-utils";

// Import our abstractions
import { QuoteViewer } from "@/features/quotes/QuoteViewer";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import useTemplates from "@/hooks/use-templates";
import { useProductsLookup } from "@/hooks/use-products";
import { useServices } from "@/hooks/use-services";
import { useCustomParts } from "@/hooks/use-custom-parts";

export default function QuoteTemplatesPage() {
  const { t } = useTranslation();
  const [, navigate] = useLocation();
  const [viewTemplateData, setViewTemplateData] = useState<any>(null);
  const [viewTemplateDialogOpen, setViewTemplateDialogOpen] = useState(false);

  // Data hooks
  const { templates, isLoading } = useTemplates();

  // Get services and custom parts for pricing calculations
  const {
    services,
    isLoading: servicesLoading,
    error: servicesError,
  } = useServices();
  const {
    customParts,
    isLoading: customPartsLoading,
    error: customPartsError,
  } = useCustomParts();

  // Extract product IDs from viewed template for lookup
  const viewedTemplateProductIds = useMemo(() => {
    if (!viewTemplateData?.products) return [];
    return viewTemplateData.products
      .map((p: any) => p.productId)
      .filter(Boolean);
  }, [viewTemplateData]);

  // Lookup products for template viewing
  const {
    products,
    isLoading: productsLoading,
    error: productsError,
  } = useProductsLookup(
    viewedTemplateProductIds,
    viewedTemplateProductIds.length > 0,
  );

  const handleDeleteTemplate = async () => {
    // TODO: Implement delete functionality when available
    window.alert("Delete functionality not yet implemented");
  };

  // Template list view
  const TemplatesList = () => (
    <>
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">{t("pages.admin.quoteTemplates.quoteTemplates")}</h1>
          <p className="text-muted-foreground mt-1">
            {t("pages.admin.quoteTemplates.manageYourQuoteTemplates")}
          </p>
        </div>
        <div className="text-right">
          <p className="text-sm text-muted-foreground mb-2">
            {t("pages.admin.quoteTemplates.wantToCreateA")}
          </p>
          <Button onClick={() => navigate("/quotes/new")}>
            {t("pages.admin.quoteTemplates.createNewQuote")}
          </Button>
        </div>
      </div>

      <Separator />

      {isLoading ? (
        <div className="text-center p-8">{t("pages.admin.quoteTemplates.loadingTemplates")}</div>
      ) : templates && templates.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {templates
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((template) => (
              <Card
                key={template.uid}
                data-testid="quote-template-card"
                className="overflow-hidden hover:shadow-md transition-shadow"
              >
                <CardHeader className="pb-3">
                  <div className="flex justify-between items-start">
                    <CardTitle className="line-clamp-1">
                      {template.name || t("pages.admin.quoteTemplates.nameNotFound")}
                    </CardTitle>
                    <span className="bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded-full">
                      {t("common.template")}
                    </span>
                  </div>
                </CardHeader>
                <CardContent className="pb-2">
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        {t("pages.admin.quoteTemplates.numberOfServices")}
                      </span>
                      <span className="font-medium">
                        {template.quoteServices
                          ? template.quoteServices.length
                          : 0}
                      </span>
                    </div>

                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t("pages.admin.quoteTemplates.createdOn")}</span>
                      <span className="text-sm">
                        {formatDate(template.creationTime)}
                      </span>
                    </div>

                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t("pages.admin.quoteTemplates.createdBy")}</span>
                      <span className="text-sm">
                        {template.createdByName || t("common.unknown")}
                      </span>
                    </div>
                  </div>
                </CardContent>
                <CardFooter className="pt-3 flex flex-col gap-2">
                  <Button
                    size="sm"
                    className="w-full"
                    data-testid="use-quote-template-button"
                    onClick={() =>
                      navigate(`/quotes/new?template=${template.uid}`)
                    }
                  >
                    {t("pages.admin.quoteTemplates.useQuoteTemplate")}
                  </Button>
                  <div className="flex gap-2 w-full">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      data-testid="view-template-button"
                      onClick={() => {
                        setViewTemplateData(template);
                        setViewTemplateDialogOpen(true);
                      }}
                    >
                      {t("pages.admin.quoteTemplates.viewTemplate")}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteTemplate()}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardFooter>
              </Card>
            ))}
        </div>
      ) : (
        <div className="text-center p-12 border rounded-lg bg-muted/10">
          <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-xl font-medium mb-2">{t("pages.admin.quoteTemplates.noTemplatesYet")}</h3>
          <p className="text-muted-foreground">
            {t("pages.admin.quoteTemplates.templatesAreCreatedWhen")}
          </p>
        </div>
      )}
    </>
  );

  return (
    <Layout headerContent={
      <div className="flex items-center gap-2 text-sm">
        <span className="text-muted-foreground">{t("common.admin")}</span>
        <span className="text-muted-foreground">/</span>
        <span className="font-semibold">{t("pages.admin.quoteTemplates.quoteTemplates")}</span>
      </div>
    }>
      <div className="space-y-6">
        <TemplatesList />

        {/* View Template Dialog */}
        {viewTemplateData && (
          <Dialog
            open={viewTemplateDialogOpen}
            onOpenChange={setViewTemplateDialogOpen}
          >
            <DialogContent
              className="sm:max-w-[900px] max-h-[90vh] p-0 flex flex-col"
              hideCloseButton
            >
              <div className="flex-1 overflow-y-auto p-6">
                <QuoteViewer
                  data={viewTemplateData}
                  viewMode="internal"
                  products={products}
                  services={services || []}
                  customParts={customParts || []}
                  productsLoading={productsLoading}
                  servicesLoading={servicesLoading}
                  customPartsLoading={customPartsLoading}
                  productsError={productsError}
                  servicesError={servicesError as Error | null}
                  customPartsError={customPartsError as Error | null}
                />
              </div>
              <div className="border-t bg-background px-6 py-4">
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setViewTemplateDialogOpen(false)}
                    size="lg"
                    className="flex-1"
                  >
                    {t("common.close")}
                  </Button>
                  <Button
                    onClick={() => {
                      navigate(`/quotes/new?template=${viewTemplateData.uid}`);
                      setViewTemplateDialogOpen(false);
                    }}
                    size="lg"
                    className="flex-1"
                  >
                    {t("pages.admin.quoteTemplates.useTemplate")}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </Layout>
  );
}
